// Piani.tsx — Base, Silver, Gold: cosa cambia. L'acquisto passa dallo store (app) o dalla pagina di pagamento di
// RevenueCat (web). v024: sul sito una carta per piano con prezzo (dalle impostazioni app.prezzo_*), scelta
// mensile/annuale in alto e il pulsante dentro la carta; nelle app il prezzo lo mostra lo store.
import { Palco } from "../componenti/Palco.tsx";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { registraRinunciaRecesso, useAccount } from "../servizi/account.ts";
import { eBooleano, eNumero, eTesto, leggiImpostazione } from "../servizi/impostazioni.ts";
import { BASE_API, IMP_APP, PERCORSO_TERMINI, PIATTAFORMA } from "../costanti.ts";
import { Indietro } from "../componenti/Indietro.tsx";
import { tastiRadio } from "../componenti/tastiRadio.ts";

// v019: i temi non sono più un vantaggio dei piani (un solo aspetto per tutti).
const VOCI = ["partite", "pubblicita", "filtri", "esclusivi", "torneo", "quattro"] as const;
const PIANI = ["base", "silver", "gold"] as const;
// Cosa offre ogni piano per ogni voce (chiavi dei testi in piani.valore_*, oppure una chiave intera con il punto).
const VALORI: Record<(typeof VOCI)[number], Record<(typeof PIANI)[number], string>> = {
  partite: { base: "quota", silver: "illimitate", gold: "illimitate" },
  pubblicita: { base: "si", silver: "no", gold: "no" },
  filtri: { base: "filtri_base", silver: "filtri_tutti", gold: "filtri_tutti" },
  esclusivi: { base: "social.crediti", silver: "social.crediti", gold: "quasi_tutti" },
  torneo: { base: "no", silver: "si", gold: "si" },
  quattro: { base: "no", silver: "no", gold: "si" },
};

/** Indirizzo della pagina di pagamento web (RevenueCat Web Purchase Link) per questo utente. */
/** I pacchetti dell'offerta «default» di RevenueCat (identificativi dei pacchetti, uguali in sandbox e live). */
export const PACCHETTI_WEB = [
  { id: "silver_annual", piano: "silver", periodo: "annuale" },
  { id: "silver_monthly", piano: "silver", periodo: "mensile" },
  { id: "gold_annual", piano: "gold", periodo: "annuale" },
  { id: "gold_monthly", piano: "gold", periodo: "mensile" },
] as const;

/** Il link di acquisto con il pacchetto già scelto (parametro package_id dei Web Purchase Links). */
export function conPacchetto(link: string, pacchetto: string): string {
  return `${link}${link.includes("?") ? "&" : "?"}package_id=${encodeURIComponent(pacchetto)}`;
}

export function linkAcquistoWeb(base: string, idAcquisti: string): string | null {
  if (!/^https:\/\//.test(base)) return null;
  return `${base.replace(/\/+$/, "")}/${encodeURIComponent(idAcquisti)}`;
}

export function Piani({ onIndietro, onAccedi }: { onIndietro: () => void; onAccedi: () => void }) {
  const { t, i18n } = useTranslation();
  const account = useAccount();
  const gold = leggiImpostazione(IMP_APP.goldInVendita, false, eBooleano);
  const linkBase = leggiImpostazione(IMP_APP.acquistoWebLink, "", eTesto);
  const piani = PIANI.filter((p) => p !== "gold" || gold);
  const attuale = account.tipo === "registrato" ? account.io.piano : "base";
  const link = account.tipo === "registrato" ? linkAcquistoWeb(linkBase, account.io.idAcquisti) : null;
  const goldEsclusivi = leggiImpostazione(IMP_APP.goldFiltriEsclusivi, true, eBooleano);
  const gruppiAttivi = leggiImpostazione(IMP_APP.gruppiAttivi, true, eBooleano);
  // La tabella segue le impostazioni: senza esclusivi in Gold, anche Gold li prende con i crediti; partite a 4 spente
  // dal pannello = «in arrivo».
  const valore = (v: (typeof VOCI)[number], p: (typeof PIANI)[number]): string =>
    v === "esclusivi" && p === "gold" && !goldEsclusivi ? "social.crediti" : v === "quattro" && p === "gold" && !gruppiAttivi ? "in_arrivo" : VALORI[v][p];
  // Chi è in Base vede tutti i pacchetti; chi ha Silver solo quelli Gold (passaggio a Gold), se Gold è in vendita.
  const pacchetti = PACCHETTI_WEB.filter((k) => (attuale === "base" ? k.piano !== "gold" || gold : attuale === "silver" && gold && k.piano === "gold"));
  // v021 (scelte legali 4 e 8): sul sito solo dai 18 anni e con la spunta del recesso; senza spunta niente pagamento.
  const [recesso, setRecesso] = useState(false);
  // pacchetto scelto prima della spunta: appena si spunta la casella si va al pagamento (stesso tocco, niente popup bloccati)
  const [inAttesa, setInAttesa] = useState<string | null>(null);
  const [erroreAcquisto, setErroreAcquisto] = useState(false);
  const [periodo, setPeriodo] = useState<"mensile" | "annuale">("annuale");
  const spunta = useRef<HTMLInputElement>(null);
  const minoreSito = account.tipo === "registrato" && account.io.puoAcquistoSito === false;
  const web = PIATTAFORMA === "web";
  const acquistoSito = web && account.tipo === "registrato" && !minoreSito && !!link;
  const euro = new Intl.NumberFormat(i18n.language, { style: "currency", currency: "EUR" });
  const prezzo = (p: "silver" | "gold", per: "mensile" | "annuale"): number =>
    leggiImpostazione(`app.prezzo_${p}_${per}`, 0, eNumero);

  const compra = (id: string, spuntata = recesso) => {
    if (!link) return;
    if (!spuntata) {
      // prima la spunta: la casella si illumina e prende il fuoco
      setInAttesa(id);
      spunta.current?.focus();
      spunta.current?.scrollIntoView({ block: "center", behavior: "smooth" });
      return;
    }
    // v021: prima si registra la spunta del recesso sul server (prova della rinuncia), poi il pagamento.
    setInAttesa(null);
    const url = conPacchetto(link, id);
    const finestra = window.open("", "_blank");
    setErroreAcquisto(false);
    registraRinunciaRecesso(id, t("piani.recesso_spunta"), i18n.language)
      .then(() => {
        if (finestra) finestra.location.href = url;
        else window.location.href = url;
      })
      .catch(() => {
        finestra?.close();
        setErroreAcquisto(true);
      });
  };

  return (
    <div className="pagina pagina-account ld-pagina">
      <header className="ld-barra">
        <Indietro onClick={onIndietro} />
        <span />
      </header>
      <main className="account-centro">
        <Palco mascotte="caso" colore="oro" titolo={t("piani.titolo")} compatto />
        {web && (
          <div className="ld-periodo" role="radiogroup" aria-label={t("piani.titolo")} onKeyDown={tastiRadio}>
            {(["mensile", "annuale"] as const).map((per) => (
              <button
                key={per}
                type="button"
                role="radio"
                aria-checked={periodo === per}
                tabIndex={periodo === per ? 0 : -1}
                className="ld-periodo-voce"
                onClick={() => setPeriodo(per)}
              >
                {t(`piani.scheda_${per}`)}
                {per === "annuale" && <span className="ld-periodo-regalo">{t("piani.due_mesi")}</span>}
              </button>
            ))}
          </div>
        )}
        {/* v019: una carta per piano (Silver, Gold) con quello che dà in più rispetto a Base; Base in fondo. */}
        <div className="ld-piani">
          {piani
            .filter((p): p is "silver" | "gold" => p !== "base")
            .map((p) => {
              const cifra = web ? prezzo(p, periodo) : 0;
              const pacchetto = pacchetti.find((k) => k.piano === p && k.periodo === periodo);
              return (
                <section key={p} className={`ld-piano ld-piano-${p}`} aria-current={p === attuale ? "true" : undefined}>
                  <span className="ld-piano-luce" aria-hidden="true" />
                  <h2 className="ld-piano-nome">
                    {t(`piani.nome_${p}`)}
                    {p === attuale ? (
                      <span className="etichetta-piccola">{t("piani.attuale")}</span>
                    ) : (
                      p === "gold" && <span className="etichetta-piccola">{t("piani.completo")}</span>
                    )}
                  </h2>
                  {cifra > 0 && (
                    <p className="ld-piano-prezzo">
                      <strong>{euro.format(cifra)}</strong>
                      <span>{t(periodo === "mensile" ? "piani.al_mese" : "piani.all_anno")}</span>
                      {periodo === "annuale" && <em>{t("piani.equivale", { prezzo: euro.format(Math.floor((cifra / 12) * 100) / 100) })}</em>}
                    </p>
                  )}
                  <ul className="ld-piano-voci">
                    {VOCI.filter((v) => valore(v, p) !== valore(v, "base")).map((v) => (
                      <li key={v}>
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M4 12l5 5L20 6" />
                        </svg>
                        <span>
                          <strong>{t(`piani.voce_${v}`)}</strong> · {t(valore(v, p).includes(".") ? valore(v, p) : `piani.valore_${valore(v, p)}`)}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {acquistoSito && pacchetto && (
                    <button type="button" className="pulsante pulsante-grande ld-piano-compra" data-pacchetto={pacchetto.id} onClick={() => compra(pacchetto.id)}>
                      {t("piani.scegli", { piano: t(`piani.nome_${p}`) })}
                    </button>
                  )}
                </section>
              );
            })}
          <section className="ld-piano ld-piano-base">
            <h2 className="ld-piano-nome">
              {t("piani.nome_base")}
              {attuale === "base" && <span className="etichetta-piccola">{t("piani.attuale")}</span>}
            </h2>
            <p className="ld-piano-base-testo">
              {VOCI.map((v) => `${t(`piani.voce_${v}`)}: ${t(valore(v, "base").includes(".") ? valore(v, "base") : `piani.valore_${valore(v, "base")}`)}`).join(" · ")}
            </p>
          </section>
        </div>
        {!web && <p className="nota-centrata">{t("piani.annuale")}</p>}
        {gold && !gruppiAttivi && <p className="nota-centrata">{t("piani.gold_presto")}</p>}
        {(attuale === "base" || pacchetti.length > 0) &&
          (account.tipo !== "registrato" ? (
            <button type="button" className="pulsante pulsante-primario pulsante-grande" onClick={onAccedi}>
              {t("piani.accedi_per_abbonarti")}
            </button>
          ) : web && minoreSito ? (
            <p className="nota-centrata ld-piani-minore" role="status">
              {t("piani.sito_minore")}
            </p>
          ) : acquistoSito ? (
            <div className="scelta-acquisto">
              {/* v043: perché c'è la casella (i tester la trovavano «preoccupante» senza spiegazione) */}
              <p className="nota ld-recesso-perche">{t("piani.recesso_perche")}</p>
              <label className={`ld-recesso${inAttesa && !recesso ? " ld-recesso-manca" : ""}`}>
                <input
                  ref={spunta}
                  type="checkbox"
                  checked={recesso}
                  onChange={(e) => {
                    setRecesso(e.target.checked);
                    if (e.target.checked && inAttesa) compra(inAttesa, true);
                  }}
                />
                <span>{t("piani.recesso_spunta")}</span>
              </label>
              {inAttesa && !recesso && (
                <p className="nota-centrata ld-recesso-serve" role="alert">
                  {t("piani.recesso_prima")}
                </p>
              )}
              {erroreAcquisto && (
                <p className="admin-errore" role="alert">
                  {t("account.errore_generico")}
                </p>
              )}
              <p className="nota-centrata">{t("piani.ricevuta")}</p>
              <p className="nota-centrata">{t("piani.sito_18")}</p>
              <a className="nota-centrata" href={`${BASE_API}${PERCORSO_TERMINI}`} target="_blank" rel="noopener">
                {t("legale.termini")}
              </a>
            </div>
          ) : (
            <p className="nota-centrata" role="status">
              {t("piani.acquisto_non_disponibile")}
            </p>
          ))}
        {account.tipo === "registrato" && attuale === "base" && <p className="nota-centrata">{t("piani.dopo_acquisto")}</p>}
      </main>
    </div>
  );
}
